// ╔══════════════════════════════════════════════════════════════════════════╗
/* ║ COPIAR ARQUIVO → src/services/stockMovementsService.js                  ║
 * ║                                                                          ║
 * ║ GS://PROTOCOLO                                                           ║
 * ║ Dossiê de Projeto e Colaboração: Ecossistema GoldenSisters              ║
 * ║ Arquivo: C:\Users\Usuario\GoldenSisters_Project\goldensisters_admin_web ║
 * ║ \src\services\stockMovementsService.js                                   ║
 * ║ Versão: 1.1.0 – Reservas de Montagem com runTransaction + reservedStock  ║
 * ║ Data: 20/12/2025                                                         ║
 * ║ Responsável: ChatGPT (conforme Protocolo)                                ║
 * ║                                                                          ║
 * ║ Mudanças desta versão:                                                   ║
 * ║ • MANTIDO: registerInventoryMovementAndUpdateStock (NF/PDV) intacto.     ║
 * ║ • MANTIDO: recordInventoryMovementRaw (logs sem alterar saldo).          ║
 * ║ • MANTIDO: Funções getMovementsBy... para não quebrar relatórios.        ║
 * ║ • ADICIONADO: Tipos RESERVA_MONTAGEM, ESTORNO_RESERVA, CONFIRMACAO...    ║
 * ║ • ADICIONADO: reserveStockForAssembly (incrementa reservedStock).        ║
 * ║ • ADICIONADO: releaseOrConfirmReserve (confirma ou estorna reserva).     ║
 * ║ • SEGURANÇA: Todas as novas operações usam runTransaction.               ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  runTransaction,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit as fsLimit,
} from 'firebase/firestore';

import { db } from '../firebase/config';

import {
  INVENTORY_MOVEMENTS_COLLECTION,
  INVENTORY_MOVEMENT_TYPES,
  INVENTORY_MOVEMENT_MODEL,
} from './gs_nf_module_dataModel';

/**
 * ATENÇÃO (consistência):
 * - O ideal é também adicionar estes tipos no gs_nf_module_dataModel (fonte oficial).
 * - Aqui fazemos um "fallback" para evitar crash caso o dataModel ainda não tenha sido atualizado.
 */
const EXTENDED_MOVEMENT_TYPES = {
  ...INVENTORY_MOVEMENT_TYPES,
  RESERVA_MONTAGEM:
    INVENTORY_MOVEMENT_TYPES?.RESERVA_MONTAGEM || 'RESERVA_MONTAGEM',
  ESTORNO_RESERVA:
    INVENTORY_MOVEMENT_TYPES?.ESTORNO_RESERVA || 'ESTORNO_RESERVA',
  CONFIRMACAO_MONTAGEM:
    INVENTORY_MOVEMENT_TYPES?.CONFIRMACAO_MONTAGEM || 'CONFIRMACAO_MONTAGEM',
};

/**
 * Cria um objeto de movimento a partir do modelo oficial,
 * preenchendo apenas os campos conhecidos e deixando o resto
 * com o default do INVENTORY_MOVEMENT_MODEL.
 */
function buildMovementPayload({
  productId,
  sku,
  movementType,
  quantityBefore,
  quantityAfter,
  quantityDelta,
  nfId = null,
  nfItemId = null,
  lotId = null,
  bathOperationId = null,
  assemblyId = null,
  saleId = null,
  notes = '',
  createdByUserId = '',
}) {
  const base = {
    ...INVENTORY_MOVEMENT_MODEL,
  };

  return {
    ...base,
    productId: productId ?? '',
    sku: sku ?? '',
    movementType: movementType || EXTENDED_MOVEMENT_TYPES.NF_ENTRADA,
    quantityBefore: Number.isFinite(quantityBefore) ? Number(quantityBefore) : 0,
    quantityAfter: Number.isFinite(quantityAfter) ? Number(quantityAfter) : 0,
    quantityDelta: Number.isFinite(quantityDelta) ? Number(quantityDelta) : 0,

    nfId: nfId ?? null,
    nfItemId: nfItemId ?? null,
    lotId: lotId ?? null,
    bathOperationId: bathOperationId ?? null,
    assemblyId: assemblyId ?? null,
    saleId: saleId ?? null,

    notes: notes || '',
    createdAt: serverTimestamp(),
    createdByUserId: createdByUserId || '',
  };
}

/**
 * Registra APENAS o documento de movimento de estoque, sem alterar
 * nenhum campo de produto. Útil para logs manuais ou casos onde
 * o estoque é calculado em outro fluxo.
 */
export async function recordInventoryMovementRaw(options) {
  const {
    productId,
    sku,
    movementType,
    quantityBefore,
    quantityAfter,
    quantityDelta,
    nfId,
    nfItemId,
    lotId,
    bathOperationId,
    assemblyId,
    saleId,
    notes,
    createdByUserId,
  } = options;

  const payload = buildMovementPayload({
    productId,
    sku,
    movementType,
    quantityBefore,
    quantityAfter,
    quantityDelta,
    nfId,
    nfItemId,
    lotId,
    bathOperationId,
    assemblyId,
    saleId,
    notes,
    createdByUserId,
  });

  const colRef = collection(db, INVENTORY_MOVEMENTS_COLLECTION);
  const docRef = await addDoc(colRef, payload);

  return {
    id: docRef.id,
    ...payload,
  };
}

/**
 * Serviço transacional completo:
 * - Lê o produto na coleção indicada (ex.: finished_products, raw_products, commercial_products, products).
 * - Calcula quantityBefore e quantityAfter.
 * - Atualiza o campo stock do produto.
 * - Registra o INVENTORY_MOVEMENT correspondente.
 *
 * Regras:
 * • Se o produto não existir:
 *   - Se quantityDelta < 0 → erro (não pode tirar de estoque inexistente).
 *   - Se quantityDelta > 0 → considera quantityBefore = 0 e cria stock = quantityDelta.
 * • Não permite quantityAfter < 0 (proteção rígida).
 */
export async function registerInventoryMovementAndUpdateStock({
  productCollection,
  productId,
  movementType,
  quantityDelta,
  nfId = null,
  nfItemId = null,
  lotId = null,
  bathOperationId = null,
  assemblyId = null,
  saleId = null,
  notes = '',
  createdByUserId = '',
}) {
  if (!productCollection || !productId) {
    throw new Error(
      '[stockMovementsService] productCollection e productId são obrigatórios para movimentação.'
    );
  }

  if (!Number.isFinite(quantityDelta) || quantityDelta === 0) {
    throw new Error(
      '[stockMovementsService] quantityDelta deve ser um número não nulo.'
    );
  }

  /**
   * Segurança: normaliza movementType para:
   * - aceitar valor direto (ex: "NF_ENTRADA") OU chave do enum.
   * - fallback seguro para AJUSTE_MANUAL/NF_ENTRADA.
   */
  const movementTypeSafe = (() => {
    if (!movementType) {
      return (
        EXTENDED_MOVEMENT_TYPES.AJUSTE_MANUAL || EXTENDED_MOVEMENT_TYPES.NF_ENTRADA
      );
    }

    // Se for exatamente um dos valores do enum
    const values = Object.values(EXTENDED_MOVEMENT_TYPES || {});
    if (values.includes(movementType)) return movementType;

    // Se for uma chave do enum (ex: "NF_ENTRADA")
    if (EXTENDED_MOVEMENT_TYPES[movementType]) return EXTENDED_MOVEMENT_TYPES[movementType];

    return (
      EXTENDED_MOVEMENT_TYPES.AJUSTE_MANUAL || EXTENDED_MOVEMENT_TYPES.NF_ENTRADA
    );
  })();

  const movementColRef = collection(db, INVENTORY_MOVEMENTS_COLLECTION);
  const productDocRef = doc(db, productCollection, productId);

  return runTransaction(db, async (tx) => {
    const snap = await tx.get(productDocRef);

    let quantityBefore = 0;
    let sku = '';

    if (snap.exists()) {
      const data = snap.data() || {};
      quantityBefore = Number.isFinite(data.stock) ? Number(data.stock) : 0;
      sku = data.sku || '';
    } else {
      // Produto ainda não existe
      if (quantityDelta < 0) {
        throw new Error(
          `[stockMovementsService] Estoque insuficiente: tentativa de saída (${quantityDelta}) para produto inexistente (${productCollection}/${productId}).`
        );
      }
      quantityBefore = 0;
      sku = '';
    }

    const quantityAfter = quantityBefore + quantityDelta;

    if (quantityAfter < 0) {
      throw new Error(
        `[stockMovementsService] Estoque insuficiente: resultaria em quantidade negativa. Atual: ${quantityBefore}, Delta: ${quantityDelta}.`
      );
    }

    // Atualiza estoque do produto
    const productUpdate = {
      stock: quantityAfter,
      updatedAt: serverTimestamp(),
    };

    tx.set(productDocRef, productUpdate, { merge: true });

    // Registra o movimento
    const movementPayload = buildMovementPayload({
      productId,
      sku,
      movementType: movementTypeSafe,
      quantityBefore,
      quantityAfter,
      quantityDelta,
      nfId,
      nfItemId,
      lotId,
      bathOperationId,
      assemblyId,
      saleId,
      notes,
      createdByUserId,
    });

    const movementDocRef = doc(movementColRef);
    tx.set(movementDocRef, movementPayload);

    return {
      movementId: movementDocRef.id,
      productId,
      productCollection,
      quantityBefore,
      quantityAfter,
      quantityDelta,
    };
  });
}

/**
 * NOVO: Reserva estoque para montagem (sem tocar no stock).
 *
 * Regra de Ouro:
 * - Antes de reservar, valida: (stock - reservedStock) >= qty
 * - Incrementa reservedStock
 * - Registra movimento: RESERVA_MONTAGEM
 *
 * Observação importante:
 * - Aqui, quantityBefore/After/Delta representam o RESERVED (para auditoria).
 * - Se você tem relatórios que somam quantityDelta de TODOS os tipos,
 *   ajuste para ignorar tipos de reserva.
 */
export async function reserveStockForAssembly({
  productCollection,
  productId,
  quantity,
  assemblyId = null,
  notes = '',
  createdByUserId = '',
}) {
  if (!productCollection || !productId) {
    throw new Error(
      '[stockMovementsService] productCollection e productId são obrigatórios para reserva.'
    );
  }

  const qty = Number(quantity);
  if (!Number.isFinite(qty) || qty <= 0) {
    throw new Error('[stockMovementsService] quantity deve ser > 0 para reserva.');
  }

  const movementColRef = collection(db, INVENTORY_MOVEMENTS_COLLECTION);
  const productDocRef = doc(db, productCollection, productId);

  return runTransaction(db, async (tx) => {
    const snap = await tx.get(productDocRef);
    if (!snap.exists()) {
      throw new Error(
        `[stockMovementsService] Produto não encontrado para reserva (${productCollection}/${productId}).`
      );
    }

    const data = snap.data() || {};
    const stock = Number.isFinite(data.stock) ? Number(data.stock) : 0;
    const reservedStock = Number.isFinite(data.reservedStock)
      ? Number(data.reservedStock)
      : 0;

    const available = stock - reservedStock;

    if (available < qty) {
      throw new Error(
        `[stockMovementsService] Reserva negada: disponível ${available}, solicitado ${qty}. (stock=${stock}, reservedStock=${reservedStock})`
      );
    }

    const reservedBefore = reservedStock;
    const reservedAfter = reservedStock + qty;

    tx.set(
      productDocRef,
      {
        reservedStock: reservedAfter,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    const movementPayload = buildMovementPayload({
      productId,
      sku: data.sku || '',
      movementType: EXTENDED_MOVEMENT_TYPES.RESERVA_MONTAGEM,
      quantityBefore: reservedBefore,
      quantityAfter: reservedAfter,
      quantityDelta: qty,
      assemblyId,
      notes: notes || 'Reserva para montagem (reservedStock +).',
      createdByUserId,
    });

    const movementDocRef = doc(movementColRef);
    tx.set(movementDocRef, movementPayload);

    return {
      movementId: movementDocRef.id,
      productId,
      productCollection,
      stock,
      reservedBefore,
      reservedAfter,
      reservedDelta: qty,
      availableBefore: available,
      availableAfter: (stock - reservedAfter),
    };
  });
}

/**
 * NOVO: Confirma ou estorna uma reserva.
 *
 * Cenário A (Confirmar):
 * - reservedStock -= qty
 * - stock -= qty
 * - movimento: CONFIRMACAO_MONTAGEM
 *
 * Cenário B (Cancelar/Estornar):
 * - reservedStock -= qty
 * - stock NÃO muda
 * - movimento: ESTORNO_RESERVA
 *
 * Segurança:
 * - Valida reservedStock >= qty
 * - No confirm, valida stock >= qty e impede stock negativo.
 *
 * Observação:
 * - Para CONFIRMAÇÃO, quantityBefore/After/Delta refletem o STOCK (efeito real).
 * - Para ESTORNO, quantityBefore/After/Delta refletem o RESERVED (liberação).
 */
export async function releaseOrConfirmReserve({
  productCollection,
  productId,
  quantity,
  mode, // 'confirm' | 'cancel'
  assemblyId = null,
  notes = '',
  createdByUserId = '',
}) {
  if (!productCollection || !productId) {
    throw new Error(
      '[stockMovementsService] productCollection e productId são obrigatórios para release/confirm.'
    );
  }

  const qty = Number(quantity);
  if (!Number.isFinite(qty) || qty <= 0) {
    throw new Error('[stockMovementsService] quantity deve ser > 0.');
  }

  const normalizedMode = String(mode || '').toLowerCase();
  if (normalizedMode !== 'confirm' && normalizedMode !== 'cancel') {
    throw new Error(
      '[stockMovementsService] mode inválido. Use "confirm" ou "cancel".'
    );
  }

  const movementColRef = collection(db, INVENTORY_MOVEMENTS_COLLECTION);
  const productDocRef = doc(db, productCollection, productId);

  return runTransaction(db, async (tx) => {
    const snap = await tx.get(productDocRef);
    if (!snap.exists()) {
      throw new Error(
        `[stockMovementsService] Produto não encontrado (${productCollection}/${productId}).`
      );
    }

    const data = snap.data() || {};
    const sku = data.sku || '';
    const stockBefore = Number.isFinite(data.stock) ? Number(data.stock) : 0;
    const reservedBefore = Number.isFinite(data.reservedStock)
      ? Number(data.reservedStock)
      : 0;

    if (reservedBefore < qty) {
      throw new Error(
        `[stockMovementsService] Operação negada: reservedStock (${reservedBefore}) menor que qty (${qty}).`
      );
    }

    const reservedAfter = reservedBefore - qty;

    if (normalizedMode === 'confirm') {
      // Confirma montagem: baixa real no stock
      const stockAfter = stockBefore - qty;

      if (stockAfter < 0) {
        throw new Error(
          `[stockMovementsService] Confirmação negada: stock insuficiente. Atual=${stockBefore}, qty=${qty}.`
        );
      }

      tx.set(
        productDocRef,
        {
          reservedStock: reservedAfter,
          stock: stockAfter,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // Movimento de confirmação: reflete STOCK
      const movementPayload = buildMovementPayload({
        productId,
        sku,
        movementType: EXTENDED_MOVEMENT_TYPES.CONFIRMACAO_MONTAGEM,
        quantityBefore: stockBefore,
        quantityAfter: stockAfter,
        quantityDelta: -qty,
        assemblyId,
        notes: notes || 'Confirmação de montagem (reservedStock - e stock -).',
        createdByUserId,
      });

      const movementDocRef = doc(movementColRef);
      tx.set(movementDocRef, movementPayload);

      return {
        movementId: movementDocRef.id,
        productId,
        productCollection,
        mode: 'confirm',
        stockBefore,
        stockAfter,
        reservedBefore,
        reservedAfter,
        availableBefore: (stockBefore - reservedBefore),
        availableAfter: (stockAfter - reservedAfter),
      };
    }

    // cancel / estorno: apenas libera a reserva
    tx.set(
      productDocRef,
      {
        reservedStock: reservedAfter,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    // Movimento de estorno: reflete RESERVED
    const movementPayload = buildMovementPayload({
      productId,
      sku,
      movementType: EXTENDED_MOVEMENT_TYPES.ESTORNO_RESERVA,
      quantityBefore: reservedBefore,
      quantityAfter: reservedAfter,
      quantityDelta: -qty,
      assemblyId,
      notes: notes || 'Estorno/cancelamento de reserva (reservedStock -).',
      createdByUserId,
    });

    const movementDocRef = doc(movementColRef);
    tx.set(movementDocRef, movementPayload);

    return {
      movementId: movementDocRef.id,
      productId,
      productCollection,
      mode: 'cancel',
      stockBefore,
      stockAfter: stockBefore,
      reservedBefore,
      reservedAfter,
      availableBefore: (stockBefore - reservedBefore),
      availableAfter: (stockBefore - reservedAfter),
    };
  });
}

/**
 * Lista movimentos de estoque por produto (ID), com opcional filtro por tipo.
 * Útil para telas de auditoria de produto.
 */
export async function getMovementsByProduct({
  productId,
  movementType = null,
  max = 50,
}) {
  if (!productId) return [];

  const colRef = collection(db, INVENTORY_MOVEMENTS_COLLECTION);

  const constraints = [where('productId', '==', productId)];

  if (movementType) {
    constraints.push(where('movementType', '==', movementType));
  }

  constraints.push(orderBy('createdAt', 'desc'));
  constraints.push(fsLimit(max));

  const q = query(colRef, ...constraints);
  const snap = await getDocs(q);

  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Lista movimentos por SKU (opcionalmente filtrando por tipo).
 * Útil para casos em que migrarmos entre coleções, mas mantivermos
 * o SKU como chave de rastreio.
 */
export async function getMovementsBySku({
  sku,
  movementType = null,
  max = 50,
}) {
  if (!sku) return [];

  const colRef = collection(db, INVENTORY_MOVEMENTS_COLLECTION);
  const constraints = [where('sku', '==', sku)];

  if (movementType) {
    constraints.push(where('movementType', '==', movementType));
  }

  constraints.push(orderBy('createdAt', 'desc'));
  constraints.push(fsLimit(max));

  const q = query(colRef, ...constraints);
  const snap = await getDocs(q);

  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Lista movimentos por contexto de processo:
 * - nfId, lotId, assemblyId, bathOperationId, saleId (qualquer combinação).
 */
export async function getMovementsByContext({
  nfId = null,
  lotId = null,
  assemblyId = null,
  bathOperationId = null,
  saleId = null,
  movementType = null,
  max = 100,
}) {
  const colRef = collection(db, INVENTORY_MOVEMENTS_COLLECTION);

  const constraints = [];

  if (nfId) constraints.push(where('nfId', '==', nfId));
  if (lotId) constraints.push(where('lotId', '==', lotId));
  if (assemblyId) constraints.push(where('assemblyId', '==', assemblyId));
  if (bathOperationId)
    constraints.push(where('bathOperationId', '==', bathOperationId));
  if (saleId) constraints.push(where('saleId', '==', saleId));
  if (movementType) constraints.push(where('movementType', '==', movementType));

  if (constraints.length === 0) {
    // Se nada foi informado, não faz sentido pegar "tudo"
    return [];
  }

  constraints.push(orderBy('createdAt', 'desc'));
  constraints.push(fsLimit(max));

  const q = query(colRef, ...constraints);
  const snap = await getDocs(q);

  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Busca um movimento específico pelo ID.
 */
export async function getMovementById(movementId) {
  if (!movementId) return null;

  const docRef = doc(db, INVENTORY_MOVEMENTS_COLLECTION, movementId);
  const snap = await getDoc(docRef);

  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

// Export default no padrão do projeto (sem export anônimo).
const stockMovementsService = {
  recordInventoryMovementRaw,
  registerInventoryMovementAndUpdateStock,

  // NOVOS (Montagem/Reserva)
  reserveStockForAssembly,
  releaseOrConfirmReserve,

  // Consultas
  getMovementsByProduct,
  getMovementsBySku,
  getMovementsByContext,
  getMovementById,

  INVENTORY_MOVEMENT_TYPES: EXTENDED_MOVEMENT_TYPES,
};

export default stockMovementsService;
